import { extractTextFromResult, type ChatOrchestrationWebSocketPayload } from "../../../services/chatService";
import { getTableCellDisplay, normalizeTablePayload } from "../../../utils/tablePayload";
import { resolveRenderType } from "../../../utils/widgetDisplay";

type CopyableMessage = {
  role: "user" | "assistant";
  content?: string;
  error?: string;
  payload?: Pick<ChatOrchestrationWebSocketPayload, "type" | "outputFormat" | "message" | "result"> | null;
};

function pushUnique(parts: string[], value: string | undefined) {
  const text = (value || "").replace(/\u00a0/g, " ").trim();
  if (!text || parts.includes(text)) return;
  parts.push(text);
}

function htmlToPlainText(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|h1|h2|h3|h4|li|tr|section)>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function clarificationLines(result: unknown): string {
  if (!result || typeof result !== "object") return "";
  const questions = (result as { questions?: unknown }).questions;
  if (!Array.isArray(questions)) return "";
  return questions
    .map((q) => {
      if (!q || typeof q !== "object" || typeof (q as { text?: unknown }).text !== "string") return "";
      return (q as { text: string }).text.trim();
    })
    .filter(Boolean)
    .join("\n");
}

function tableToText(result: unknown): string {
  const table = normalizeTablePayload(result);
  if (!table) return "";
  const header = table.columns.map((col) => col.header || col.field || "").join("\t");
  const lines = table.rows.map((row) =>
    table.columns
      .map((col) => getTableCellDisplay(row, col.field || col.header, col.header, col.format))
      .join("\t")
  );
  return [header, ...lines].filter((line) => line.trim() !== "").join("\n");
}

/** Plain text a chat bubble can place on the clipboard. Empty while the assistant is still thinking. */
export function copyableChatText(msg: CopyableMessage): string {
  if (msg.role === "user") return (msg.content || "").trim();

  const parts: string[] = [];
  pushUnique(parts, msg.error);
  pushUnique(parts, msg.content);
  const payload = msg.payload;
  if (!payload) return parts.join("\n\n");

  pushUnique(parts, payload.message);
  const type = resolveRenderType(payload.type, payload.outputFormat, payload.result);

  if (type === "text" || type === "error") {
    const body = extractTextFromResult(payload.result);
    pushUnique(parts, /<[a-z][\s\S]*>/i.test(body) ? htmlToPlainText(body) : body);
  } else if (type === "html") {
    const raw = extractTextFromResult(payload.result);
    pushUnique(parts, htmlToPlainText(raw || (typeof payload.result === "string" ? payload.result : "")));
  } else if (type === "table") {
    const table = tableToText(payload.result);
    if (table) pushUnique(parts, table);
    else {
      const raw = extractTextFromResult(payload.result);
      pushUnique(parts, /<[a-z][\s\S]*>/i.test(raw) ? htmlToPlainText(raw) : "");
    }
  } else if (type === "json") {
    const body = extractTextFromResult(payload.result);
    if (body) pushUnique(parts, body);
    else if (payload.result != null) {
      try {
        pushUnique(parts, JSON.stringify(payload.result, null, 2));
      } catch {
        pushUnique(parts, String(payload.result));
      }
    }
  } else if (type === "clarification") {
    pushUnique(parts, clarificationLines(payload.result));
  }

  return parts.join("\n\n");
}
